import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  ExpandMore,
  Download,
  PieChart as PieChartIcon,
  ShowChart,
  CloudSync,
  Visibility,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { formatCurrency, formatPercentage } from '../../../utils/CurrencyFormat';
import {
  NetWorthSnapshot,
  computeGrossAssets,
  computeNetWorth,
  assetBreakdown,
  newSnapshotId,
  goldComputedTotal,
  computeUsStockInr,
  europeIncomeInr,
  cryptoInr,
} from '../../../models/NetWorthTypes';
import { snapshotsToCsv, downloadCsv } from '../../../utils/netWorthCsv';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getInsights } from '../../../services/request/Dashboard';
import { getCapitalsDashboard } from '../../../services/request/CapitalManagement';
import {
  getNetWorthSnapshotsPage,
  getNetWorthSnapshotsBulk,
  createNetWorthSnapshot,
  updateNetWorthSnapshot,
  deleteNetWorthSnapshot,
} from '../../../services/request/NetWorth';
import { PaginationConst } from '../../../services/constants/AppConstant';
import { PageHeader, MetricStrip, SectionPanel, EmptyState } from '../../../shared-components/ui';
import { useBusinessDomain } from '../../../contexts/BusinessDomainContext';
import AssetProgressCharts from './AssetProgressCharts';

/** Max rows fetched for charts; when total snapshots ≤ this, the table is sliced client-side (no second list request). */
const NETWORTH_CHARTS_FETCH_SIZE = 2000;

type NetWorthDialogMode = 'new' | 'edit' | 'view';

function parseDashboardTotalOutstanding(res: unknown): number | undefined {
  const r = res as { data?: { totalOutstanding?: number }; totalOutstanding?: number };
  const d = r?.data ?? r;
  const inner = d as { totalOutstanding?: number; data?: { totalOutstanding?: number } };
  const v = inner?.totalOutstanding ?? inner?.data?.totalOutstanding;
  if (v === undefined || v === null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Unsaved form state while Add/Edit snapshot dialog is open */
const NETWORTH_FORM_DRAFT_KEY = 'whatzneu_networth_form_draft_v1';

function loadFormDraft(): NetWorthSnapshot | null {
  try {
    const raw = localStorage.getItem(NETWORTH_FORM_DRAFT_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as NetWorthSnapshot;
    if (!s || typeof s.recordDate !== 'string' || typeof s.id !== 'string') return null;
    return s;
  } catch {
    return null;
  }
}

function clearFormDraft(): void {
  try {
    localStorage.removeItem(NETWORTH_FORM_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

const PIE_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#d32f2f',
  '#00897b',
  '#5d4037',
  '#455a64',
  '#fbc02d',
  '#7b1fa2',
  '#c2185b',
  '#00796b',
  '#e65100',
];

function emptySnapshot(): NetWorthSnapshot {
  return {
    id: newSnapshotId(),
    recordDate: new Date().toISOString().slice(0, 10),
  };
}

function NumField({
  label,
  value,
  onChange,
  helperText,
  readOnly,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  helperText?: string;
  readOnly?: boolean;
}) {
  return (
    <TextField
      label={label}
      type={readOnly ? 'text' : 'number'}
      size="small"
      fullWidth
      value={value === undefined || Number.isNaN(value) ? (readOnly ? '—' : '') : String(value)}
      onChange={(e) => {
        if (readOnly) return;
        const t = e.target.value;
        if (t === '') onChange(undefined);
        else {
          const n = parseFloat(t);
          onChange(Number.isFinite(n) ? n : undefined);
        }
      }}
      inputProps={{ readOnly: !!readOnly, step: 'any' }}
      helperText={helperText}
      sx={readOnly ? { '& .MuiInputBase-input': { cursor: 'default' } } : undefined}
    />
  );
}

const NetWorthTracker: React.FC = () => {
  const { domain } = useBusinessDomain();
  const [chartSnapshots, setChartSnapshots] = useState<NetWorthSnapshot[]>([]);
  const [tableRows, setTableRows] = useState<NetWorthSnapshot[]>([]);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(PaginationConst.PAGE_SIZE);
  const [tableTotal, setTableTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NetWorthSnapshot | null>(null);
  const [editingIsExisting, setEditingIsExisting] = useState(false);
  const [dialogMode, setDialogMode] = useState<NetWorthDialogMode>('new');
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [partnerCapitalLoading, setPartnerCapitalLoading] = useState(false);
  /** Bumps after each bulk refresh so the server-paginated table refetches when total > cap. */
  const [listRefreshEpoch, setListRefreshEpoch] = useState(0);

  const loadChartDataset = useCallback(async () => {
    setChartsLoading(true);
    setListLoading(true);
    try {
      const res = await getNetWorthSnapshotsBulk(NETWORTH_CHARTS_FETCH_SIZE);
      const rows = res?.result ?? [];
      const total = res?.totalCount ?? 0;
      setChartSnapshots(rows);
      setTableTotal(total);
      setListRefreshEpoch((e) => e + 1);
      if (total === 0) {
        setTableRows([]);
        setListLoading(false);
      }
    } catch {
      toast.error('Failed to load net worth data.');
      setChartSnapshots([]);
      setTableRows([]);
      setTableTotal(0);
      setListLoading(false);
    } finally {
      setChartsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadChartDataset();
  }, [loadChartDataset]);

  /** Client-side pages when the full dataset fits in the charts bulk response. */
  useEffect(() => {
    if (tableTotal <= 0 || tableTotal > NETWORTH_CHARTS_FETCH_SIZE) return;
    if (chartSnapshots.length !== tableTotal) return;
    const start = tablePage * tableRowsPerPage;
    setTableRows(chartSnapshots.slice(start, start + tableRowsPerPage));
    setListLoading(false);
  }, [tablePage, tableRowsPerPage, tableTotal, chartSnapshots]);

  /** Server-side pages when there are more snapshots than we load for charts. */
  useEffect(() => {
    if (tableTotal === 0 || tableTotal <= NETWORTH_CHARTS_FETCH_SIZE) return;
    let cancelled = false;
    setListLoading(true);
    (async () => {
      try {
        const res = await getNetWorthSnapshotsPage(tablePage, tableRowsPerPage);
        if (cancelled) return;
        setTableRows(res?.result ?? []);
        setTableTotal(res?.totalCount ?? 0);
      } catch {
        if (!cancelled) {
          toast.error('Failed to load net worth list.');
          setTableRows([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tablePage, tableRowsPerPage, tableTotal, listRefreshEpoch]);

  /** Persist dialog inputs locally so closing the dialog or refreshing does not lose work */
  useEffect(() => {
    if (!dialogOpen || !editing || dialogMode === 'view') return;
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(NETWORTH_FORM_DRAFT_KEY, JSON.stringify(editing));
      } catch {
        /* quota / private mode */
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [dialogOpen, editing, dialogMode]);

  const sortedAsc = useMemo(
    () =>
      [...chartSnapshots].sort((a, b) => new Date(a.recordDate).getTime() - new Date(b.recordDate).getTime()),
    [chartSnapshots]
  );

  const latest = sortedAsc.length ? sortedAsc[sortedAsc.length - 1] : null;
  const previous = sortedAsc.length > 1 ? sortedAsc[sortedAsc.length - 2] : null;

  const latestNet = latest ? computeNetWorth(latest) : 0;
  const prevNet = previous ? computeNetWorth(previous) : null;
  const momPct =
    prevNet != null && prevNet !== 0 ? ((latestNet - prevNet) / Math.abs(prevNet)) * 100 : null;

  const chartData = useMemo(
    () =>
      sortedAsc.map((s) => ({
        date: s.recordDate,
        net: computeNetWorth(s),
        gross: computeGrossAssets(s),
      })),
    [sortedAsc]
  );

  const pieData = useMemo(() => {
    if (!latest) return [];
    return assetBreakdown(latest).map((e) => ({ name: e.name, value: e.value }));
  }, [latest]);

  const openNew = () => {
    setDialogMode('new');
    setEditingIsExisting(false);
    const d = loadFormDraft();
    const resumeUnsavedNew = d && !chartSnapshots.some((r) => r.id === d.id);
    setEditing(resumeUnsavedNew ? d : emptySnapshot());
    setDialogOpen(true);
  };

  const openEdit = (s: NetWorthSnapshot) => {
    setDialogMode('edit');
    setEditingIsExisting(true);
    const d = loadFormDraft();
    const resume = d && d.id === s.id;
    setEditing(resume ? d : { ...s });
    setDialogOpen(true);
  };

  const openView = (s: NetWorthSnapshot) => {
    setDialogMode('view');
    setEditingIsExisting(true);
    setEditing({ ...s });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.recordDate) {
      toast.error('Record date is required');
      return;
    }
    try {
      if (editingIsExisting) {
        const res = await updateNetWorthSnapshot(editing.id, editing);
        if (res?.statusCode !== 200) {
          toast.error(res?.message || 'Update failed');
          return;
        }
      } else {
        const res = await createNetWorthSnapshot(editing);
        if (res?.statusCode !== 200) {
          toast.error(res?.message || 'Create failed');
          return;
        }
      }
      clearFormDraft();
      toast.success('Snapshot saved');
      closeDialog();
      await loadChartDataset();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this snapshot?')) return;
    try {
      await deleteNetWorthSnapshot(id);
      toast.info('Snapshot removed');
      await loadChartDataset();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    }
  };

  const patchEditing = (patch: Partial<NetWorthSnapshot>) => {
    setEditing((e) => (e ? { ...e, ...patch } : e));
  };

  const handleFetchCurrentOutstanding = async () => {
    setOutstandingLoading(true);
    try {
      const res = await getInsights({});
      const n = parseDashboardTotalOutstanding(res);
      if (n === undefined) {
        toast.error('Could not read total outstanding from the dashboard API.');
        return;
      }
      patchEditing({ outstanding: n });
      toast.success(`Outstanding set to ${formatCurrency(n, 'INR', true)} (lending book)`);
    } catch {
      toast.error('Failed to fetch outstanding. Check that you are signed in and the API is reachable.');
    } finally {
      setOutstandingLoading(false);
    }
  };

  const handleFetchPartnerCapital = async () => {
    setPartnerCapitalLoading(true);
    try {
      const data = await getCapitalsDashboard();
      const n = Number(data?.totalCapitalContributed);
      if (!Number.isFinite(n)) {
        toast.error('Could not read partner capital from the capitals API.');
        return;
      }
      patchEditing({ partnerCapitalLiability: n });
      toast.success(
        `Partner capital set to ${formatCurrency(n, 'INR', true)} (${data.totalPartners} partner${data.totalPartners === 1 ? '' : 's'})`
      );
    } catch {
      toast.error('Failed to fetch partner capital. Check that you are signed in and the API is reachable.');
    } finally {
      setPartnerCapitalLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await getNetWorthSnapshotsBulk(10000);
      const rows = res?.result ?? [];
      if (!rows.length) {
        toast.error('Nothing to export');
        return;
      }
      const csv = snapshotsToCsv([...rows].sort((a, b) => a.recordDate.localeCompare(b.recordDate)));
      downloadCsv(`networth-export-${new Date().toISOString().slice(0, 10)}.csv`, csv);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const gross = latest ? computeGrossAssets(latest) : 0;

  const viewOnly = dialogMode === 'view';

  return (
    <Box sx={{ pb: 4 }}>
      <ToastContainer position="top-right" autoClose={2800} />
      {(listLoading || chartsLoading) && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.modal + 2 }} />}
      <PageHeader
        eyebrow={`${domain === 'CAPITALS' ? 'Capitals' : 'Lending'} · Tools`}
        title="Personal net worth"
        subtitle="Snapshots are saved on the server. Export CSV to back up. Form drafts auto-save in this browser until you click Save."
        actions={
          <>
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>
              Add snapshot
            </Button>
            <Button variant="outlined" startIcon={<Download />} onClick={() => void exportCsv()} disabled={tableTotal === 0}>
              Export CSV
            </Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Gross assets</strong> sums: outstanding, savings, equity &amp; MF market values, fixed income
        buckets, crypto, real estate, gold (grams × rate or total INR), <strong>US stocks (Stock USD × INR/USD)</strong>,
        and other USD cash × rate. <strong>Net worth</strong> = gross − customer FD liability − partner capital
        (held in trust).
      </Alert>

      {latest && (
        <MetricStrip
          metrics={[
            {
              label: 'Latest net worth',
              value: formatCurrency(latestNet, 'INR', true),
              hint: `As of ${latest.recordDate}`,
              tone: 'info',
            },
            {
              label: 'Gross assets',
              value: formatCurrency(gross, 'INR', true),
              hint: 'Computed from buckets',
            },
            {
              label: 'Liabilities & partner capital',
              value: formatCurrency(
                (latest.customerFdLiability ?? 0) + (latest.partnerCapitalLiability ?? 0),
                'INR',
                true
              ),
              hint: `FD ${formatCurrency(latest.customerFdLiability ?? 0, 'INR', true)} · Partners ${formatCurrency(latest.partnerCapitalLiability ?? 0, 'INR', true)}`,
              tone: 'warning',
            },
            {
              label: 'Month on month',
              value: momPct == null ? '—' : formatPercentage(momPct),
              hint: 'Net change vs prior snapshot',
              tone: momPct != null && momPct < 0 ? 'error' : 'success',
            },
          ]}
        />
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <SectionPanel
            title="Net worth over time"
            subtitle="Trend across saved snapshots"
            actions={<ShowChart color="primary" fontSize="small" />}
            sx={{ height: 360 }}
          >
            <Box sx={{ px: 2.5, pb: 2.5, height: 280 }}>
              {chartData.length < 2 ? (
                <EmptyState title="Add at least two snapshots to see the trend." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1e5).toFixed(1)}L`} />
                    <ReTooltip formatter={(v: number) => formatCurrency(v, 'INR', true)} />
                    <Line type="monotone" dataKey="net" name="Net worth" stroke="#0F766E" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </SectionPanel>
        </Grid>
        <Grid item xs={12} md={5}>
          <SectionPanel
            title="Latest allocation"
            subtitle="Asset mix on the newest snapshot"
            actions={<PieChartIcon color="primary" fontSize="small" />}
            sx={{ height: 360 }}
          >
            <Box sx={{ px: 2.5, pb: 2.5, height: 280 }}>
              {!pieData.length ? (
                <EmptyState title="No asset buckets yet on the latest snapshot." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(v: number) => formatCurrency(v, 'INR', true)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </SectionPanel>
        </Grid>
      </Grid>

      <AssetProgressCharts snapshotsAsc={sortedAsc} />

      <SectionPanel title="Snapshots" subtitle={`Newest first · ${tableTotal} total`}>
          {listLoading && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Customer FD</TableCell>
                  <TableCell align="right">Partner capital</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell>Slogan</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading && tableTotal === 0 && tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState title="Loading snapshots…" />
                    </TableCell>
                  </TableRow>
                ) : !listLoading && tableTotal === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState
                        title="No data yet"
                        description="Add a snapshot to get started."
                        actionLabel="Add snapshot"
                        onAction={openNew}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.recordDate}</TableCell>
                      <TableCell align="right">{formatCurrency(computeGrossAssets(s), 'INR', true)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(s.customerFdLiability ?? 0, 'INR', true)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(s.partnerCapitalLiability ?? 0, 'INR', true)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(computeNetWorth(s), 'INR', true)}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={s.sloganOfTheMonth}
                      >
                        {s.sloganOfTheMonth || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => openView(s)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(s)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => void remove(s.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={tableTotal}
            page={tablePage}
            onPageChange={(_, newPage) => setTablePage(newPage)}
            rowsPerPage={tableRowsPerPage}
            onRowsPerPageChange={(e) => {
              setTableRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
      </SectionPanel>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'min(92vh, 880px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {viewOnly && editing
            ? `View snapshot · ${editing.recordDate}`
            : editingIsExisting
              ? 'Edit snapshot'
              : 'New snapshot'}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {editing && (
            <>
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'grey.50',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }}
              >
                <Typography variant="body1" component="div">
                  Computed gross:{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatCurrency(computeGrossAssets(editing), 'INR', true)}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary', mx: 1 }}>
                    ·
                  </Box>
                  Net:{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatCurrency(computeNetWorth(editing), 'INR', true)}
                  </Box>
                </Typography>
                {!viewOnly && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Values auto-save as a draft in this browser until you save the snapshot.
                  </Typography>
                )}
                {viewOnly && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Read-only. Use Edit to change this snapshot.
                  </Typography>
                )}
              </Box>
              <Box sx={{ overflow: 'auto', flex: '1 1 auto', px: 2, py: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Record date"
                type={viewOnly ? 'text' : 'date'}
                size="small"
                value={editing.recordDate}
                onChange={(e) => patchEditing({ recordDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: viewOnly }}
                fullWidth
              />
              <TextField
                label="Slogan of the month"
                size="small"
                value={editing.sloganOfTheMonth ?? ''}
                onChange={(e) => patchEditing({ sloganOfTheMonth: e.target.value || undefined })}
                InputProps={{ readOnly: viewOnly }}
                fullWidth
              />
              <TextField
                label="Description"
                size="small"
                multiline
                minRows={2}
                value={editing.description ?? ''}
                onChange={(e) => patchEditing({ description: e.target.value || undefined })}
                InputProps={{ readOnly: viewOnly }}
                fullWidth
                helperText={viewOnly ? undefined : 'Optional notes'}
              />

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Receivables &amp; cash</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <NumField readOnly={viewOnly}
                            label="Outstanding"
                            value={editing.outstanding}
                            onChange={(v) => patchEditing({ outstanding: v })}
                            helperText={
                              viewOnly
                                ? undefined
                                : 'Lending receivables; use the button to pull live total from the dashboard API.'
                            }
                          />
                        </Box>
                        {!viewOnly && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudSync />}
                            onClick={handleFetchCurrentOutstanding}
                            disabled={outstandingLoading}
                            sx={{ mt: { xs: 0, sm: 1 }, flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            {outstandingLoading ? 'Fetching…' : 'Fetch current outstanding'}
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Savings" value={editing.savings} onChange={(v) => patchEditing({ savings: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Salary (reference)" value={editing.salary} onChange={(v) => patchEditing({ salary: v })} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Equity &amp; mutual funds</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Equity amount invested" value={editing.equityAmountInvested} onChange={(v) => patchEditing({ equityAmountInvested: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Equity current value" value={editing.equityCurrentValue} onChange={(v) => patchEditing({ equityCurrentValue: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="MF invested" value={editing.mfAmountInvested} onChange={(v) => patchEditing({ mfAmountInvested: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="MF current value" value={editing.mfCurrentValue} onChange={(v) => patchEditing({ mfCurrentValue: v })} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Fixed income, bonds, PPF, FD &amp; Europe</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Bonds (corporate FD / bonds)" value={editing.corporateFdBonds} onChange={(v) => patchEditing({ corporateFdBonds: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="PPF" value={editing.ppf} onChange={(v) => patchEditing({ ppf: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="FD (bank FD)" value={editing.bankFd} onChange={(v) => patchEditing({ bankFd: v })} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Europe: either enter a single INR total, or EU salary + EU equity with conversion rate (INR per unit).
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly}
                        label="Europe / other (INR total)"
                        value={editing.europeIncome}
                        onChange={(v) => patchEditing({ europeIncome: v })}
                        helperText={viewOnly ? undefined : 'If set (&gt; 0), gross uses this instead of EU salary × rate + EU equity × rate.'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly} label="EU salary" value={editing.euSalary} onChange={(v) => patchEditing({ euSalary: v })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly} label="EU equity" value={editing.euEquity} onChange={(v) => patchEditing({ euEquity: v })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="EU conversion rate"
                        value={editing.euConversionRate}
                        onChange={(v) => patchEditing({ euConversionRate: v })}
                        helperText={viewOnly ? undefined : 'INR per 1 unit of EU salary and EU equity.'}
                      />
                    </Grid>
                    {viewOnly && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Europe bucket (INR) in gross: {formatCurrency(europeIncomeInr(editing), 'INR', true)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Gold, crypto, real estate, US stocks &amp; USD</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly} label="Gold (grams)" value={editing.goldGrams} onChange={(v) => patchEditing({ goldGrams: v })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="Gold conversion rate (₹/gram)"
                        value={editing.goldInrPerGram}
                        onChange={(v) => patchEditing({ goldInrPerGram: v })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="Gold total ₹ (override)"
                        value={editing.goldTotalInr}
                        onChange={(v) => patchEditing({ goldTotalInr: v })}
                        helperText={`Computed: ${formatCurrency(goldComputedTotal(editing), 'INR', true)}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="Crypto (₹ / USDT equiv.)"
                        value={editing.cryptoUsdt}
                        onChange={(v) => patchEditing({ cryptoUsdt: v })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly} label="Crypto (USD)" value={editing.cryptoUsd} onChange={(v) => patchEditing({ cryptoUsd: v })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="Crypto → INR rate"
                        value={editing.cryptoToInrRate}
                        onChange={(v) => patchEditing({ cryptoToInrRate: v })}
                        helperText={
                          viewOnly
                            ? undefined
                            : 'INR per 1 USD of crypto. If empty, US conversion rate (INR/USD) is used when Crypto (USD) is set.'
                        }
                      />
                    </Grid>
                    {viewOnly && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Crypto in gross (INR): {formatCurrency(cryptoInr(editing), 'INR', true)}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Real estate values (₹)" value={editing.realEstate} onChange={(v) => patchEditing({ realEstate: v })} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Real estate description"
                        size="small"
                        fullWidth
                        value={editing.realEstateDescription ?? ''}
                        onChange={(e) => patchEditing({ realEstateDescription: e.target.value || undefined })}
                        InputProps={{ readOnly: viewOnly }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                        US stocks
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Total INR = US stocks (dollar) × US conversion rate (INR per USD), unless you override Total ₹.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="US stocks (dollar)"
                        value={editing.usStockUsd}
                        onChange={(v) => patchEditing({ usStockUsd: v })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="US conversion rate (INR/USD)"
                        value={editing.usStockConversionRate}
                        onChange={(v) => patchEditing({ usStockConversionRate: v })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumField readOnly={viewOnly}
                        label="Total ₹ (override)"
                        value={editing.usStockTotalInr}
                        onChange={(v) => patchEditing({ usStockTotalInr: v })}
                        helperText={`Computed: ${formatCurrency(computeUsStockInr(editing), 'INR', true)}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <NumField readOnly={viewOnly} label="USD cash amount" value={editing.usdAmount} onChange={(v) => patchEditing({ usdAmount: v })} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <NumField readOnly={viewOnly} label="USD → INR rate" value={editing.usdToInrRate} onChange={(v) => patchEditing({ usdToInrRate: v })} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Insights &amp; liabilities</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Customer FD (liability)" value={editing.customerFdLiability} onChange={(v) => patchEditing({ customerFdLiability: v })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumField readOnly={viewOnly} label="Monthly interest income ₹" value={editing.monthlyInterestIncome} onChange={(v) => patchEditing({ monthlyInterestIncome: v })} />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <NumField
                            readOnly={viewOnly}
                            label="Partner capital (held in trust)"
                            value={editing.partnerCapitalLiability}
                            onChange={(v) => patchEditing({ partnerCapitalLiability: v })}
                            helperText={
                              viewOnly
                                ? 'This money belongs to capital partners and is excluded from net worth.'
                                : 'Total contributed by capital partners. Use the button to pull live total from the capitals API. Editable. Excluded from net worth.'
                            }
                          />
                        </Box>
                        {!viewOnly && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudSync />}
                            onClick={handleFetchPartnerCapital}
                            disabled={partnerCapitalLoading}
                            sx={{ mt: { xs: 0, sm: 1 }, flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            {partnerCapitalLoading ? 'Fetching…' : 'Fetch partner capital'}
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Stack>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 2, py: 1.5 }}>
          {viewOnly ? (
            <>
              <Button onClick={closeDialog}>Close</Button>
              <Button variant="contained" onClick={() => setDialogMode('edit')}>
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeDialog}>Cancel</Button>
              <Button variant="contained" onClick={() => void saveEdit()}>
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NetWorthTracker;
